import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "../../lib/firebase";
import {
  getRegisteredUsers,
  getInvitedUsers,
  getAdminUsers,
  getFacultyUsers,
} from "../../services/authService";
import moment from "moment";

const modalContent = {
  1: 'After completing another day of the challenge, how motivated are you to start the next day and try to reach your steps goal?',
  2: 'How do you rate your likelihood of reaching your steps goal today?',
  3: 'How committed do you think you are to reaching your steps goal in the next day?',
};

const FEEDBACK_COLORS = ["#6366f1", "#10b981", "#f59e42"];
const ROLE_COLORS = ["#f59e42", "#3b82f6"];


export default function Dashboard() {
  const [chartData, setChartData] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [facultyUsers, setFacultyUsers] = useState([]);
  const [feedbackData, setFeedbackData] = useState([]);

  const [hoveredBar, setHoveredBar] = useState(null);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length || !hoveredBar) return null;

    const barData = payload.find((p) => p.dataKey === hoveredBar);
    if (!barData) return null;

    const labelMap = {
      question1: { label: "Motivation", index: 1 },
      question2: { label: "Likelihood", index: 2 },
      question3: { label: "Commitment", index: 3 },
    };

    const { label: barLabel, index } = labelMap[hoveredBar];

    return (
      <div className="rounded-lg shadow-lg bg-white/90 px-4 py-2 border border-gray-200 min-w-[180px]">
        <div className="font-semibold text-gray-700 mb-1">{barLabel}</div>
        <div className="text-xs text-gray-600 mb-2">{modalContent[index]}</div>
        <div className="text-xs text-gray-400">Day: {label}</div>
        <div className="text-xs text-gray-800 font-bold">Value: {barData.value}</div>
      </div>
    );
  };

  useEffect(() => {
    async function fetchData() {
      try {
        // Steps data
        const dailyStepsSnapshot = await getDocs(
          collection(firestore, "Daily Steps")
        );
        const dailySteps = dailyStepsSnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        const stepsByDate = {};
        dailySteps.forEach((entry) => {
          if (!entry.timestamp || typeof entry.Steps !== "number") return;
          const date = moment(entry.timestamp.toDate()).format("YYYY-MM-DD");
          if (!stepsByDate[date]) stepsByDate[date] = [];
          stepsByDate[date].push(entry.Steps);
        });

        const transformedData = Object.entries(stepsByDate).map(
          ([date, stepsArray]) => ({
            date,
            avgSteps: Math.round(
              stepsArray.reduce((sum, val) => sum + val, 0) / stepsArray.length
            ),
          })
        );

        transformedData.sort((a, b) => new Date(a.date) - new Date(b.date));
        setChartData(transformedData);

        // Users data
        const invitedRes = await getInvitedUsers();
        const invitedData = await invitedRes.json();
        setInvitedUsers(invitedData.users || []);

        const registeredRes = await getRegisteredUsers();
        const registeredData = await registeredRes.json();
        setRegisteredUsers(registeredData.users || []);

        const adminRes = await getAdminUsers();
        const adminData = await adminRes.json();
        setAdminUsers(adminData.users || []);

        const facultyRes = await getFacultyUsers();
        const facultyData = await facultyRes.json();
        setFacultyUsers(facultyData.users || []);

        // Feedback data for ALL users
        const feedbackSnapshot = await getDocs(collection(firestore, "Review"));
        const feedback = feedbackSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Group feedback by day and average
        const feedbackByDay = {};
        feedback.forEach((fb) => {
          if (!fb.day) return;
          const dayKey = `Day ${fb.day}`;
          if (!feedbackByDay[dayKey]) {
            feedbackByDay[dayKey] = { question1: [], question2: [], question3: [] };
          }
          if (typeof fb.question1 === "number") feedbackByDay[dayKey].question1.push(fb.question1);
          if (typeof fb.question2 === "number") feedbackByDay[dayKey].question2.push(fb.question2);
          if (typeof fb.question3 === "number") feedbackByDay[dayKey].question3.push(fb.question3);
        });

        const aggregatedFeedback = Object.entries(feedbackByDay).map(([day, vals]) => ({
          day,
          question1: vals.question1.length ? (vals.question1.reduce((a, b) => a + b, 0) / vals.question1.length).toFixed(2) : null,
          question2: vals.question2.length ? (vals.question2.reduce((a, b) => a + b, 0) / vals.question2.length).toFixed(2) : null,
          question3: vals.question3.length ? (vals.question3.reduce((a, b) => a + b, 0) / vals.question3.length).toFixed(2) : null,
        }));

        aggregatedFeedback.sort((a, b) => {
          const dayA = parseInt(a.day.replace(/\D/g, ""));
          const dayB = parseInt(b.day.replace(/\D/g, ""));
          return dayA - dayB;
        });

        setFeedbackData(aggregatedFeedback);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }

    fetchData();
  }, []);

  const userComparisonData = [
    {
      name: "Participants",
      Invited: invitedUsers.length,
      Registered: registeredUsers.length,
    },
  ];

  const userRoleData = [
    { name: "Admin Users", value: adminUsers.length },
    { name: "Faculty Users", value: facultyUsers.length },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br  via-white to-green-50 relative pb-12">
      {/* Decorative SVG Background */}
      {/* <svg
        className="absolute top-0 left-0 w-full h-64 opacity-10 pointer-events-none"
        viewBox="0 0 1440 320"
      >
        <path
          fill="#6366f1"
          fillOpacity="1"
          d="M0,128L48,138.7C96,149,192,171,288,154.7C384,139,480,85,576,80C672,75,768,117,864,138.7C960,160,1056,160,1152,154.7C1248,149,1344,139,1392,133.3L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
        ></path>
      </svg> */}

      {/* Header */}
      <header className="relative z-10 max-w-7xl mx-auto 2xl:pt-12 2xl:pb-8 lg:pt-6 lg:pb-2 px-4">
        <h1 className="text-4xl font-extrabold text-gray-900 mb-2 flex items-center gap-3">
          <span className="bg-gradient-to-r from-indigo-500 to-green-400 bg-clip-text text-transparent">
            {/* SVG icon for analytics */}
            <svg
              className="inline-block h-8 w-8 mr-2 align-text-bottom"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-6 0h6"
              />
            </svg>
            Analytics Dashboard
          </span>
        </h1>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 px-4 max-w-7xl mx-auto">
        {/* Chart Card Template */}
        {/* Set min-h-[400px] for all cards to ensure equal height */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-indigo-100 min-h-[400px] flex flex-col">
          <div className="mb-6 flex items-center gap-3">
            <span className="bg-indigo-100 p-2 rounded-xl">
              <svg className="h-7 w-7 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
              </svg>
            </span>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Average Daily Steps</h2>
              <p className="text-sm text-gray-500">Engagement trend across all users</p>
            </div>
          </div>
          <div className="flex-1 flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" />
                <YAxis allowDecimals={false} stroke="#6b7280" />
                <Tooltip contentStyle={{ borderRadius: "8px" }} />
                <Legend />
                <Line type="monotone" dataKey="avgSteps" stroke="#6366f1" strokeWidth={3} activeDot={{ r: 8 }} name="Avg Steps" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-green-100 min-h-[400px] flex flex-col">
          <div className="mb-6 flex items-center gap-3">
            <span className="bg-green-100 p-2 rounded-xl">
              <svg className="h-7 w-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M9 20H4v-2a3 3 0 015.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </span>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Invited vs Registered</h2>
              <p className="text-sm text-gray-500">Comparison of user acquisition</p>
            </div>
          </div>
          <div className="flex-1 flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={userComparisonData} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis allowDecimals={false} stroke="#6b7280" />
                <Tooltip contentStyle={{ borderRadius: "8px" }} />
                <Legend />
                <Bar dataKey="Invited" fill="#6366f1" name="Invited Users" />
                <Bar dataKey="Registered" fill="#10b981" name="Registered Users"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border min-h-[400px] flex flex-col">
          <div className="mb-6 flex items-center gap-3">
            <span className="bg-yellow-100 p-2 rounded-xl">
              <svg className="h-7 w-7 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </span>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Admin vs Faculty Users</h2>
              <p className="text-sm text-gray-500">Distribution of user roles</p>
            </div>
          </div>
          <div className="flex-1 flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={userRoleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                  {userRoleData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={ROLE_COLORS[index % ROLE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: "8px" }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border min-h-[400px] flex flex-col">
  <div className="mb-6 flex items-center gap-3">
    <span className="bg-blue-100 p-2 rounded-xl">
      <svg className="h-7 w-7 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V10a2 2 0 012-2h2m2-4h4a2 2 0 012 2v4H7V6a2 2 0 012-2z" />
      </svg>
    </span>
    <div>
      <h2 className="text-2xl font-bold text-gray-800">Feedback Review Analysis</h2>
      <p className="text-sm text-gray-500">Daily feedback trends for motivation, likelihood, and commitment</p>
    </div>
  </div>
  <div className="flex-1 flex items-center">
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={feedbackData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }} barCategoryGap="20%">
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="day" stroke="#6b7280" />
        <YAxis allowDecimals={false} stroke="#6b7280" />
        <Tooltip content={<CustomTooltip />} />
        <Legend
              formatter={(value) =>
                ({ question1: "Motivation", question2: "Likelihood", question3: "Commitment" }[value] || value)
              }
            />
            <Bar
              dataKey="question1"
              fill={FEEDBACK_COLORS[0]}
              name="Motivation"
              onMouseEnter={() => setHoveredBar("question1")}
              onMouseLeave={() => setHoveredBar(null)}
            />
            <Bar
              dataKey="question2"
              fill={FEEDBACK_COLORS[1]}
              name="Likelihood"
              onMouseEnter={() => setHoveredBar("question2")}
              onMouseLeave={() => setHoveredBar(null)}
            />
            <Bar
              dataKey="question3"
              fill={FEEDBACK_COLORS[2]}
              name="Commitment"
              onMouseEnter={() => setHoveredBar("question3")}
              onMouseLeave={() => setHoveredBar(null)}
            />
          </BarChart>
    </ResponsiveContainer>
  </div>
</div>

      </div>
 </div>
  );
}
